import {useCallback, useMemo} from 'react';
import {OccasionBrandType} from '../../../gql/pwa/graphql';
import {BrandOption} from './use_brands_hook';
import {useSearchParams} from 'react-router-dom';
import {MultiValue} from 'react-select';

export interface ModelOption {
    value: string;
    label?: string;
    meta: OccasionBrandType;
}
export interface GroupedModelOption {
    readonly label?: string;
    readonly options: ReadonlyArray<ModelOption>;
}
export function useModels(
    brands: Array<OccasionBrandType | null> | null | undefined,
    models: string[] | undefined,
    selectedOccasionBrandOption: BrandOption | null,
) {
    const modelOptions: GroupedModelOption[] = useMemo(
        () =>
            selectedOccasionBrandOption
                ? [
                      {
                          label: selectedOccasionBrandOption.meta.name ?? '',
                          options:
                              selectedOccasionBrandOption.meta.models?.map((model) => {
                                  return {
                                      value: model ?? '',
                                      label: model ?? '',
                                      meta: selectedOccasionBrandOption.meta,
                                  };
                              }) ?? [],
                      },
                  ]
                : brands
                      ?.filter((b): b is NonNullable<typeof b> => b != null)
                      .map((ob) => {
                          return {
                              label: ob.name ?? '',
                              options:
                                  ob.models?.map((model) => {
                                      return {
                                          value: model ?? '',
                                          label: model ?? '',
                                          meta: ob,
                                      };
                                  }) ?? [],
                          };
                      }) ?? [],
        [selectedOccasionBrandOption, brands],
    );

    const selectedModels: ModelOption[] = useMemo(
        () =>
            models
                ?.flatMap((model) => modelOptions?.flatMap((mo) => mo.options.find((m) => m.value === model)))
                .filter((m): m is NonNullable<typeof m> => m != null) ?? [],
        [models, modelOptions],
    );

    const [, setSearchParams] = useSearchParams();
    const onSetModelsCallback = useCallback(
        (values: MultiValue<ModelOption>) => {
            setSearchParams((current) => {
                if (values.length > 0 && values[0].meta.name) {
                    current.set('brands', values[0].meta.name);
                }

                const models = values.map((o) => o.value);
                if (models.length > 0) {
                    current.delete('models');
                    for (const model of models) {
                        current.append('models', model);
                    }
                } else {
                    current.delete('models');
                }
                current.delete('page');
                return current;
            });
        },
        [setSearchParams],
    );
    const onClearModelCallback = useCallback(
        (model: string) => {
            setSearchParams((current) => {
                current.delete('models', model);
                current.delete('page');
                return current;
            });
        },
        [setSearchParams],
    );

    return {modelOptions, selectedModels, onSetModelsCallback, onClearModelCallback};
}
